// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useId, type ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'

/**
 * D-UI-3 — the one modal dialog. Replaces hand-rolled overlay divs: MUI traps focus inside, returns it to
 * the trigger on close, and labels the dialog by its title; the first text field takes focus on open. While `busy`, neither Esc nor a backdrop click
 * closes it (a request in flight must not be orphaned behind a closed dialog).
 */
export interface AppDialogProps {
  open: boolean
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  onClose: () => void
  busy?: boolean
  /** 'alertdialog' for destructive confirmations (screen readers announce it as urgent). */
  role?: 'dialog' | 'alertdialog'
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg'
}

const VISIBLE = ':not([disabled]):not([aria-hidden="true"]):not([tabindex="-1"])'
const FIRST_FIELD = `.MuiDialogContent-root textarea${VISIBLE}, .MuiDialogContent-root input${VISIBLE}:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])`

export default function AppDialog({ open, title, children, actions, onClose, busy = false, role = 'dialog', maxWidth = 'sm' }: AppDialogProps) {
  const titleId = useId()
  return (
    <Dialog open={open} onClose={() => { if (!busy) onClose() }} aria-labelledby={titleId}
      fullWidth maxWidth={maxWidth}
      // MUI focuses the dialog box itself, so `autoFocus` on a field never took: a keyboard user had to Tab
      // before typing (Hold pilot H4). Once open, the first visible text field takes focus — unless focus
      // is already on a field inside. No field: the box keeps it.
      slotProps={{
        paper: { role },
        transition: { onEntered: (node: HTMLElement) => {
          const active = document.activeElement
          if (active && node.contains(active) && active.matches('input, textarea, select')) return
          node.querySelector<HTMLElement>(FIRST_FIELD)?.focus()
        } },
      }}>
      <DialogTitle id={titleId} sx={{ fontSize: 15.5, fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      {children !== undefined && <DialogContent sx={{ fontSize: 13.5, lineHeight: 1.55 }}>{children}</DialogContent>}
      {actions && <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>}
    </Dialog>
  )
}
